import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import ReactTooltip from 'react-tooltip';
import shortid from 'shortid';
import { fromJS } from 'immutable';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  Brush,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { capitalize, startCase } from 'lodash';
import {
  getRecordLevelTypeDisplayName,
  getRecordTypeCode,
  getRecordTypeId,
  RECORD_TYPE_ACCOUNT,
  RECORD_TYPE_CONTACT,
  RECORD_TYPE_LEAD,
  SIMPLE_DATETIME_FORMAT,
} from '../../config/app_config';
import { addCommas } from '../../utils/common';

export default class OrderReport extends React.Component {
  static propTypes = {
    orderMatchReportData: PropTypes.array.isRequired,
    orderMatchReportSeriesData: PropTypes.array.isRequired,
    orderSavedSearchData: PropTypes.array.isRequired,
    customerRecordMetadata: PropTypes.array.isRequired,
    fieldGroupsAgg: PropTypes.array.isRequired,
  };

  static fieldAbbrMap = {
    ACCOUNT: {
      N: 'Name',
      A1: 'Address 1',
      A2: 'Address 2',
      C: 'City',
      S: 'State',
      Z: 'Zip',
      CO: 'Country',
      SA1: 'Secondary Address 1',
      SA2: 'Secondary Address 2',
      SC: 'Secondary City',
      SS: 'Secondary State',
      SZ: 'Secondary Zip',
      SCO: 'Secondary Country',
      P: 'Phone',
      U: 'URL',
    },
    CONTACT: {
      FN: 'First Name',
      LN: 'Last Name',
      A1: 'Address 1',
      A2: 'Address 2',
      C: 'City',
      S: 'State',
      Z: 'Zip',
      CO: 'Country',
      COM: 'Company',
      SA1: 'Secondary Address 1',
      SA2: 'Secondary Address 2',
      SC: 'Secondary City',
      SS: 'Secondary State',
      SZ: 'Secondary Zip',
      SCO: 'Secondary Country',
      P: 'Phone',
      M: 'Mobile',
      E: 'Email',
      T: 'Title',
      D: 'Department',
    },
  };

  getRecordCount = () => (
    <table className="table table-sm">
      <thead>
        <tr>
          <th scope="col">Type</th>
          <th scope="col">Record Count</th>
        </tr>
      </thead>
      <tbody>
        {this.props.orderMatchReportData.map(r => (
          <tr key={`tr-${r.name}`}>
            <th scope="row">{r.name}</th>
            <td>{r.value}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );

  getRecordDistribution = () => {
    const COLORS = ['#8884d8', '#82ca9d', '#2c7be5', '#39afd1', '#6e84a3', '#283e59'];
    const RADIAN = Math.PI / 180;
    const renderCustomizedLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }) => {
      const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
      const x = cx + radius * Math.cos(-midAngle * RADIAN);
      const y = cy + radius * Math.sin(-midAngle * RADIAN);

      return (
        <text
          x={x}
          y={y}
          fill="white"
          textAnchor={x > cx ? 'start' : 'end'}
          dominantBaseline="central"
        >
          {`${(percent * 100).toFixed(0)}%`}
        </text>
      );
    };

    return (
      <div style={{ width: '99%', height: 400 }}>
        <ResponsiveContainer>
          <PieChart>
            <Pie
              isAnimationActive={false}
              nameKey="name"
              dataKey="value"
              data={this.props.orderMatchReportData}
              outerRadius={150}
              fill="#8884d8"
              label={renderCustomizedLabel}
              labelLine={false}
            >
              {this.props.orderMatchReportData.map((entry, index) => (
                <Cell key={entry.name} fill={COLORS[index % COLORS.length]} />
              ))}
            </Pie>
            <Tooltip />
          </PieChart>
        </ResponsiveContainer>
      </div>
    );
  };

  getRecordSeries = () => (
    <ResponsiveContainer>
      <AreaChart
        maxBarSize={10}
        data={this.props.orderMatchReportSeriesData}
        margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
      >
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis dataKey="date" />
        <YAxis />
        <Tooltip />
        <Legend />
        <Area
          dataKey="accountsMatched"
          name="Accounts Matched"
          stackId="a"
          fill="#8884d8"
          type="monotone"
        />
        <Area
          dataKey="accountsProspected"
          name="Accounts Prospected"
          stackId="a"
          fill="#82ca9d"
          type="monotone"
        />
        <Area
          dataKey="contactsMatched"
          name="Contacts Matched"
          stackId="a"
          fill="#2c7be5"
          type="monotone"
        />
        <Area
          dataKey="contactsProspected"
          name="Contacts Prospected"
          stackId="a"
          fill="#39afd1"
          type="monotone"
        />
        <Area
          dataKey="leadsMatched"
          name="Leads Matched"
          stackId="a"
          fill="#6e84a3"
          type="monotone"
        />
        <Area
          dataKey="leadsProspected"
          name="Leads Prospected"
          stackId="a"
          fill="#283e59"
          type="monotone"
        />
        <Area
          dataKey="leadAccountsMatched"
          name="Lead Accounts Matched"
          stackId="a"
          fill="#ffa500"
          type="monotone"
        />
        <Area
          dataKey="leadAccountsProspected"
          name="Leads Accounts Prospected"
          stackId="a"
          fill="#ffd700"
          type="monotone"
        />
        <Brush dataKey="date" height={30} stroke="#8884d8" />
      </AreaChart>
    </ResponsiveContainer>
  );

  getSavedSearch = () => {
    const renderSearchParams = (uiSearch, searchType, objectName) => {
      const type = objectName ? `${searchType}.${objectName}` : searchType;
      const search = uiSearch.get(type);
      const searchParams = search
        .valueSeq()
        .filter(p => p.has('params'))
        .map(p =>
          p
            .get('params')
            .valueSeq()
            .map(v => (
              <span key={shortid.generate()}>
                <span key={shortid.generate()} className="badge badge-primary">
                  {v.get('displayName')}:{' '}
                  {v
                    .get('displayValue')
                    .split(', ')
                    .reduce((a, c) => [a, <br key={shortid.generate()} />, c])}
                </span>
              </span>
            ))
            .reduce((acc, curr) => [acc, <br key={shortid.generate()} />, curr])
        )
        .reduce((acc, curr) => [
          acc,
          <span key={shortid.generate()}>
            <br key={shortid.generate()} />
            <span key={shortid.generate()} className="badge badge-pill badge-danger">
              --- OR ---
            </span>
            <br key={shortid.generate()} />
          </span>,
          curr,
        ]);
      const parentSearchParams = search
        .valueSeq()
        .filter(p => p.has('parentParams'))
        .map(p =>
          p
            .get('parentParams')
            .valueSeq()
            .map(v => (
              <span key={shortid.generate()} className="badge badge-pill badge-primary">
                {`${v.get('displayName')}: ${v.get('displayValue')}`}
              </span>
            ))
            .reduce((acc, curr) => [acc, '\u00A0', curr])
        )
        .reduce((acc, curr) => [
          acc,
          <span key={shortid.generate()}>
            <br key={shortid.generate()} />
            <span key={shortid.generate()} className="badge badge-pill badge-danger">
              OR
            </span>
            <br key={shortid.generate()} />
          </span>,
          curr,
        ]);
      return (
        <span key="userSearchParams">
          {searchParams ? <span key="searchParams">{searchParams}</span> : null}
          {parentSearchParams ? <span key="parentSearchParams">{parentSearchParams}</span> : null}
        </span>
      );
    };

    return (
      <table className="table table-sm table-xs">
        <thead>
          <tr>
            <th scope="col">Type</th>
            <th scope="col">Auto Add</th>
            <th scope="col">Add As</th>
            <th scope="col">Associated Type</th>
            <th scope="col">Created By</th>
            <th scope="col">Search Name</th>
            <th scope="col">Parameters</th>
            <th scope="col">Created On</th>
          </tr>
        </thead>
        <tbody>
          {this.props.orderSavedSearchData.map(r => (
            <tr key={`tr-${r.id}`}>
              <td>{getRecordLevelTypeDisplayName(r.recordLevelTypeId)}</td>
              <td>{r.autoProspectTypeId ? 'True' : 'False'}</td>
              <td>
                {r.autoProspectTypeId ? getRecordLevelTypeDisplayName(r.autoProspectTypeId) : ''}
              </td>
              <td>{r.objectName}</td>
              <td>{r.customerId}</td>
              <td>{r.name}</td>
              <td>
                {r.uiSavedSearch
                  ? renderSearchParams(
                      fromJS(JSON.parse(r.uiSavedSearch)),
                      getRecordTypeCode(r.recordLevelTypeId),
                      r.objectName
                    )
                  : ''}
              </td>
              <td>{moment(r.createdOn).format(SIMPLE_DATETIME_FORMAT)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  };

  getPercent = (val, total) => {
    const x = Number(val);
    const y = Number(total);
    if (!x || !y || y === 0) {
      return 0;
    }
    return ((x * 100) / y).toFixed(2);
  };

  getTotalRetrieved = (type, count) => (
    <div className="card-border">
      <div className="card-block">
        <div className="row align-items-center">
          <div className="col-sm-12">
            <h8 className="card-title text-uppercase text-muted mb-2">Total {type} Retrieved</h8>
            <p className="h2 mb-0">{count ? addCommas(count) : '-'}</p>
          </div>
        </div>
      </div>
    </div>
  );

  getMatchPercent = recordType => {
    const { customerRecordMetadata, orderMatchReportData } = this.props;
    const retrieved =
      customerRecordMetadata.find(x => x.recordTypeId === getRecordTypeId(recordType)) || 0;
    const matched = orderMatchReportData.find(
      r => r.name.startsWith(capitalize(`${recordType}s`)) && r.name.includes('Matched')
    ) || { value: 0 };
    const prospected = orderMatchReportData.find(
      r => r.name.startsWith(capitalize(`${recordType}s`)) && r.name.includes('Prospected')
    ) || { value: 0 };

    if (recordType === RECORD_TYPE_LEAD) {
        // Include lead accounts with lead totals
        const otherType = 'Lead Accounts';
        const otherMatched = orderMatchReportData.find(
            r => r.name.startsWith(otherType) && r.name.includes('Matched')
        ) || { value: 0 };
        const otherProspected = orderMatchReportData.find(
            r => r.name.startsWith(otherType) && r.name.includes('Prospected')
        ) || { value: 0 };
        matched.value += otherMatched.value;
        prospected.value += otherProspected.value;
    }

    const percent = this.getPercent(matched.value, Math.max(retrieved.total - prospected.value, 0));
    const matchRate = !percent || Number.isNaN(percent) ? '-' : percent;

    return (
      <div key={`${recordType}MatchRate`} className="card-border">
        <div className="card-block">
          <div className="row align-items-center">
            <div className="col-sm-12">
              <h8 className="card-title text-uppercase text-muted mb-2">{recordType} Match Rate</h8>
              <p className="h2 mb-0">{matchRate}%</p>
            </div>
          </div>
        </div>
      </div>
    );
  };

  getCustomerRecordBarChart = data => (
    <div style={{ width: '95%', height: 150 }}>
      <ResponsiveContainer>
        <BarChart data={data}>
          <Bar dataKey="percent" fill="#8884d8" />
          <XAxis dataKey="field" type="category" tick={false} />
          <Tooltip />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );

  getCustomerRecordMetadata = recordType => {
    const { customerRecordMetadata } = this.props;
    const m = customerRecordMetadata.find(x => x.recordTypeId === getRecordTypeId(recordType));
    const prefix = recordType === RECORD_TYPE_ACCOUNT ? 'account' : 'contact';
    const data = m
      ? Object.entries(m)
          .filter(e => e[0].startsWith(prefix))
          .sort((a, b) => (Number(a[1]) > Number(b[1]) ? -1 : 1))
          .map(e => ({
            field: startCase(e[0].replace(prefix, '')),
            count: e[1],
            percent: Number(this.getPercent(e[1], m.total)),
          }))
      : [];
    return m ? (
      <div>
        {this.getCustomerRecordBarChart(data)}
        <table className="table table-sm table-xs">
          <thead>
            <tr>
              <th scope="col">Field Type</th>
              <th scope="col">Present In (%)</th>
            </tr>
          </thead>
          <tbody>
            {data.map(d => (
              <tr key={`${recordType}${d.field}`}>
                <th title={`${d.percent}% of records have ${d.field} field`}>{d.field}</th>
                <td title={`Count ${d.count}`}>{d.percent}%</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    ) : (
      'Stats Not Available'
    );
  };

  getFieldGroupsAgg = recordType => {
    const { fieldGroupsAgg, customerRecordMetadata } = this.props;
    const m = customerRecordMetadata.find(x => x.recordTypeId === getRecordTypeId(recordType));
    const data =
      fieldGroupsAgg && m
        ? fieldGroupsAgg
            .filter(x => x.recordTypeId === getRecordTypeId(recordType))
            .map(d => ({
              field: d.fieldGroup,
              count: d.count,
              percent: Number(this.getPercent(d.count, m.total)),
            }))
        : [];
    return fieldGroupsAgg && m ? (
      <div>
        {this.getCustomerRecordBarChart(data)}
        <table className="table table-sm table-xs">
          <thead>
            <tr>
              <th scope="col">Groups</th>
              <th scope="col">Percent (%)</th>
            </tr>
          </thead>
          <tbody>
            {data.map(d => (
              <tr key={`${recordType}${d.field}`}>
                <th title={`${d.percent}% of records have ${d.field} fields in each record`}>
                  {d.field
                    .split(',')
                    .map(f => (
                      <span
                        key={shortid.generate()}
                        className="badge badge-pill badge-outline-primary"
                      >
                        {f}
                      </span>
                    ))
                    .reduce((acc, curr) => [acc, '\u00A0', curr])}
                </th>
                <td title={`Count ${d.count}`}>{d.percent}%</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    ) : (
      'Stats Not Available'
    );
  };

  getCustomerFieldDataStats = () => {
    const { customerRecordMetadata } = this.props;
    return (
      <div className="card-block">
        <div className="row">
          {[RECORD_TYPE_ACCOUNT, RECORD_TYPE_CONTACT, RECORD_TYPE_LEAD].map(r => {
            const m = customerRecordMetadata
              ? customerRecordMetadata.find(x => x.recordTypeId === getRecordTypeId(r))
              : null;
            return (
              <div key={`${r}RetrievedStats`} className="col-sm-4">
                {this.getTotalRetrieved(`${startCase(r)}s`, m ? m.total : null)}
              </div>
            );
          })}
        </div>
        <br key={shortid.generate()} />
        <div className="row">
          {[RECORD_TYPE_ACCOUNT, RECORD_TYPE_CONTACT, RECORD_TYPE_LEAD].map(r => (
            <div key={`${r}RecordStats`} className="col-sm-4">
              <div className="card-border">
                <div className="card-header">
                  <div className="card-header-title">
                    {capitalize(r)} Field Stats
                    <span
                      className="alert alert-link"
                      role="alert"
                      data-tip={`
                          Data shows population of individual fields across all retrieved ${r.toLowerCase()}s.<br/>
                          Hover on rows for more information.<br/>
                          To understand match-ability of retrieved ${r.toLowerCase()}s, see Group Distribution.`}
                      data-for="fieldStats"
                    >
                      <i className="fa fa-info-circle" aria-hidden="true" />
                    </span>
                    <ReactTooltip id="fieldStats" type="info" html />
                  </div>
                </div>
                <div className="card-block">
                  <div className="chart-1000">{this.getCustomerRecordMetadata(r)}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
        <br key={shortid.generate()} />
        <div className="row">
          {[RECORD_TYPE_ACCOUNT, RECORD_TYPE_CONTACT, RECORD_TYPE_LEAD].map(r => (
            <div key={`${r}RecordDistributionStats`} className="col-sm-4">
              <div className="card-border">
                <div className="card-header">
                  <div className="card-header-title">
                    {capitalize(r)} Field Group Distribution
                    <span
                      className="alert alert-link"
                      role="alert"
                      data-tip={`
                          Data shows distribution of retrieved ${r.toLowerCase()}s grouped by fields present in it.<br/>
                          The more fields present, the better it is for matching.<br/>
                          Groups with very few fields are unlikely to be auto-matched overnight,<br/>
                          due to low confidence, except under certain conditions.<br/>
                          Hover on rows for more information.<br/>
                          <br />
                          Field Abbreviations:<br />
                          ${Object.entries(
                            r === RECORD_TYPE_ACCOUNT
                              ? OrderReport.fieldAbbrMap.ACCOUNT
                              : OrderReport.fieldAbbrMap.CONTACT
                          )
                            .map(f => `${f[0]}: ${f[1]}`)
                            .join('<br />')}`}
                      data-for="groupDistributionStats"
                    >
                      <i className="fa fa-info-circle" aria-hidden="true" />
                    </span>
                    <ReactTooltip id="groupDistributionStats" type="info" html />
                  </div>
                </div>
                <div className="card-block">
                  <div className="chart-1000">{this.getFieldGroupsAgg(r)}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  render() {
    return (
      <div className="modal-wrap">
        <div className="container-fluid container-home">
          <div className="header">
            <div className="header-body">
              <div className="row">
                <div className="col-sm-12">
                  <h1 className="header-title">Order Report</h1>
                </div>
              </div>
            </div>
          </div>
          <div className="row">
            <div className="col-sm-8">
              <div className="card-border">
                <div className="card-header">
                  <div className="card-header-title">Order Overview</div>
                </div>
                <div className="card-block">
                  <div className="chart-top">
                    <div className="row">
                      <div className="col-sm-4">
                        {[RECORD_TYPE_ACCOUNT, RECORD_TYPE_CONTACT, RECORD_TYPE_LEAD].map(r =>
                          this.getMatchPercent(r)
                        )}
                      </div>
                      <div className="col-sm-8">{this.getRecordCount()}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-sm-4">
              <div className="card-border">
                <div className="card-header">
                  <div className="card-header-title">Record Type Distribution</div>
                </div>
                <div className="card-block">
                  <div className="chart-top">{this.getRecordDistribution()}</div>
                </div>
              </div>
            </div>
          </div>
          <br key={shortid.generate()} />
          <div className="row">
            <div className="col-sm-12">
              <div className="card-border">
                <div className="card-header">
                  <div className="card-header-title">Data Report Series View</div>
                </div>
                <div className="card-block">
                  <div className="chart-top" style={{ width: '100%', height: 400 }}>
                    {this.getRecordSeries()}
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="row">
            <div className="col-sm-12">
              <div className="card-border">
                <div className="card-header">
                  <div className="card-header-title">Customer Data Field Stats</div>
                </div>
                {this.getCustomerFieldDataStats()}
              </div>
            </div>
          </div>
          <br key={shortid.generate()} />
          <div className="row">
            <div className="col-sm-12">
              <div className="card-border">
                <div className="card-header">
                  <div className="card-header-title">Saved Search Created by Customer</div>
                </div>
                <div className="card-block">
                  <div className="chart-800">{this.getSavedSearch()}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
}
